class FeaturedBlogCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');

    if (!this.track || this.slides.length === 0) return;

    this.previousButton?.addEventListener('click', this.showPrevious);
    this.nextButton?.addEventListener('click', this.showNext);
    this.track.addEventListener('keydown', this.handleKeydown);
    this.track.addEventListener('scroll', this.scheduleStateUpdate, { passive: true });
    window.addEventListener('resize', this.handleResize);

    requestAnimationFrame(() => {
      this.activeIndex = Math.min(2, this.slides.length - 1);
      this.centerSlide(this.activeIndex, 'auto');
      this.updateState();
    });
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.showPrevious);
    this.nextButton?.removeEventListener('click', this.showNext);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    this.track?.removeEventListener('scroll', this.scheduleStateUpdate);
    window.removeEventListener('resize', this.handleResize);
    cancelAnimationFrame(this.animationFrame);
  }

  showPrevious = () => {
    this.centerSlide(Math.max(0, this.activeIndex - 1));
  };

  showNext = () => {
    this.centerSlide(Math.min(this.slides.length - 1, this.activeIndex + 1));
  };

  handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    this.centerSlide(Math.max(0, Math.min(this.slides.length - 1, this.activeIndex + direction)));
  };

  handleResize = () => {
    this.centerSlide(this.activeIndex, 'auto');
    this.scheduleStateUpdate();
  };

  centerSlide(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide || !this.track) return;

    const left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    this.activeIndex = index;
    this.track.scrollTo({ left, behavior });
    this.updateState();
  }

  scheduleStateUpdate = () => {
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = requestAnimationFrame(() => this.updateState());
  };

  updateState() {
    if (!this.track) return;

    const trackCenter = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const bounds = slide.getBoundingClientRect();
      const distance = Math.abs(bounds.left + bounds.width / 2 - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        this.activeIndex = index;
      }
    });

    this.slides.forEach((slide, index) => {
      const isActive = index === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.previousButton) {
      this.previousButton.disabled = this.activeIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.activeIndex === this.slides.length - 1;
    }
  }
}

if (!customElements.get('featured-blog-carousel')) {
  customElements.define('featured-blog-carousel', FeaturedBlogCarousel);
}
